import { Close, Info } from "@components/icons"
import { IconWrapper } from "@components/icons/utils"
import SelectInput from "@components/inputs/SelectInput"
import { useSaveVehicleToStore } from "@components/NavbarHeader/components/MenuDesktop/hooks/useSaveVehicleToStore"
import { useUpholsteryCarsForCarSelector } from "@components/NavbarHeader/components/MenuDesktop/hooks/useUpholsteryCarsForCarSelector"
import { useVehicleSelectionHandlers } from "@components/NavbarHeader/components/MenuDesktop/hooks/useVehicleSelectionHandlers"
import VerticalSpace from "@components/VerticalSpace"
import colors from "@constants/colors"
import { Tab, Tabs } from "@mui/material"
import {
  getCarsMakeModelYearTrimUrl,
  getCarsMakeModelYearUrl,
} from "@utils/urls"
import { isPdmStructureV2Enabled } from "@views/UpholsteryV2"
import { buildUpholsteryLandingHref } from "@views/InventoryChecker/helpers"
import { useFetchUpholsteryCars } from "@views/Upholstery/components/hooks/useFetchUpholsteryCars"
import { useRouter } from "next/router"
import React from "react"
import styled from "styled-components"
import {
  Body,
  CloseButton,
  Container,
  Content,
  Filter,
  Filters,
  Footer,
  Header,
  Label,
  Overlay,
} from "./sharedStyles"
import { CarSelectorProps } from "./types"

const TabsContainer = styled.div`
  margin-bottom: 24px;
  /* border-bottom: 1px solid ${colors.silver}; */
  padding: 0 40px;
`

const StyledTabs = styled(Tabs)`
  && {
    min-height: 0;
  }

  &.MuiTabs-root {
    min-height: 0;
  }

  .MuiTabs-indicator {
    display: none;
  }

  .MuiTabs-flexContainer {
    gap: 0;
    flex-wrap: nowrap;
    width: 100%;
  }
`

const StyledTab = styled(Tab)`
  && {
    font-family: "Jost", sans-serif;
    font-size: 14px;
    font-weight: 500;
    text-transform: uppercase;
    color: rgba(0, 0, 0, 0.65);
    padding: 10px 16px;
    margin-right: 2px;
    min-height: auto;
    min-width: auto;
    text-align: center;
    align-items: center;
    opacity: 1;
    position: relative;
    white-space: nowrap;
    flex-shrink: 0;
    letter-spacing: 0;
    background: #fafafa;
    border-radius: 8px 8px 0 0;
    margin-bottom: -1px;
    border: 1px solid #d9d9d9;
    border-bottom: 1px solid #d9d9d9;
    transition: all 0.2s;
    flex: 1;
  }

  &&.Mui-selected {
    color: ${colors.red};
    background: #ffffff;
    border: 1px solid #d9d9d9;
    border-bottom: 1px solid #ffffff;
  }

  &&:hover:not(.Mui-selected) {
    color: ${colors.red};
  }

  .MuiTab-wrapper {
    white-space: nowrap;
    display: inline-flex;
    align-items: center;
    font-weight: 500;
  }

  span {
    white-space: nowrap;
    display: inline;
    font-weight: 500;
  }
`

const PopularBuildsContainer = styled.div`
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  padding: 0 40px;
`

const PopularBuildsLabel = styled.div`
  font-family: "Jost", sans-serif;
  font-size: 14px;
  color: ${colors.slateDark};
  text-transform: capitalize;
`

const PopularBuildsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
`

const PopularBuildButton = styled.button<{ variant?: "classic" | "modern" }>`
  && {
    background: ${({ variant }) =>
      variant === "classic" ? colors.red : "#000000"} !important;
    color: ${colors.snow} !important;
    min-width: auto !important;
    text-transform: none !important;
  }
  padding: 12px 20px;
  border: 0;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: 0.3s ease-out;
  font-family: "Jost", sans-serif;
  white-space: nowrap;
  width: 100%;
  text-align: center;

  &:hover {
    background: ${({ variant }) =>
      variant === "classic" ? colors.red : "#000000"} !important;
    color: ${colors.snow} !important;
    opacity: 0.9;
    transform: translateY(-1px);
  }
`

// Helper function to determine if a car is classic or modern based on years
const isClassicCar = (car: any): boolean => {
  if (!car.years) return true
  const yearList = car.years.split(", ").map((y: string) => parseInt(y.trim()))
  const maxYear = Math.max(...yearList.filter((y: number) => !isNaN(y)))
  return maxYear < 2020
}

// Popular builds data (year first in labels)
const classicPopularBuilds = [
  { label: "1988 Chevy Truck", make: "chevy", model: "truck", year: "1988" },
  { label: "1975 Ford Truck", make: "ford", model: "truck", year: "1975" },
  {
    label: "1955 VW Bug Sedan",
    make: "vw",
    model: "bug",
    year: "1955",
    type: "bug-sedan",
  },
]

const modernPopularBuilds = [
  {
    label: "2024 Chevrolet Silverado 1500",
    make: "chevrolet",
    model: "silverado-1500",
    year: "2024",
  },
  {
    label: "2024 Ford F-150",
    make: "ford",
    model: "f-150",
    year: "2024",
  },
  {
    label: "2024 Toyota Tacoma",
    make: "toyota",
    model: "tacoma",
    year: "2024",
  },
]

const CarSelector: React.FC<CarSelectorProps> = ({
  isOpen,
  cars,
  makeModelOrder = {},
  onRequestClose,
  onRequestCloseMenu,
  module = "automotive",
}) => {
  const router = useRouter()
  const currentPath = router.asPath.split("?")[0].split("#")[0]
  const defaultActiveTab =
    currentPath === "/late-model" || currentPath === "/new-vehicle" ? 1 : 0
  const isV2 = isPdmStructureV2Enabled()

  // Legacy (flag off): preload Dato upholstery cars. V2 on: no-op; Layout owns PDM.
  useFetchUpholsteryCars()

  // PDM catalog load + refetch is owned by `useGlobalVehicleCatalogLoader` in Layout.
  const {
    modernCars: upholsteryModernCars,
    isUsingUpholsteryCars,
    upholsteryCars,
    catalogLoading,
  } = useUpholsteryCarsForCarSelector()

  // Hook to save vehicle selection to store
  const { saveVehicle } = useSaveVehicleToStore()

  const filteredCars = cars.filter((car) => car.module == module)
  const classicCars = filteredCars.filter(isClassicCar)

  // V2 on: PDM only (no Dato fallback). Legacy flag off: Dato upholstery or allCars.
  const modernCars = React.useMemo(() => {
    if (isV2) {
      return upholsteryModernCars
    }
    if (upholsteryModernCars.length > 0) {
      return upholsteryModernCars
    }
    return filteredCars.filter((car) => !isClassicCar(car))
  }, [isV2, upholsteryModernCars, filteredCars])

  // Classic cars state
  const [classicMake, setClassicMake] = React.useState<string>("")
  const [classicModel, setClassicModel] = React.useState<string>("")
  const [classicTrim, setClassicTrim] = React.useState<string>("")
  const [classicYear, setClassicYear] = React.useState<string>("")
  const [classicModels, setClassicModels] = React.useState<Array<any>>([])
  const [classicTrims, setClassicTrims] = React.useState<Array<any>>([])
  const [classicYears, setClassicYears] = React.useState<Array<any>>([])

  // Modern cars state
  const [modernMake, setModernMake] = React.useState<string>("")
  const [modernModel, setModernModel] = React.useState<string>("")
  const [modernTrim, setModernTrim] = React.useState<string>("")
  const [modernYear, setModernYear] = React.useState<string>("")
  const [modernModels, setModernModels] = React.useState<Array<any>>([])
  const [modernTrims, setModernTrims] = React.useState<Array<any>>([])
  const [modernYears, setModernYears] = React.useState<Array<any>>([])
  const [modernLoading, setModernLoading] = React.useState<boolean>(false)

  // Tab state
  const [activeTab, setActiveTab] = React.useState<number>(defaultActiveTab)

  // Classic vehicle selection handlers
  const {
    makes: classicMakes,
    onSelectMake: onClassicSelectMake,
    onSelectModel: onClassicSelectModel,
    onSelectTrim: onClassicSelectTrim,
  } = useVehicleSelectionHandlers({
    cars: classicCars,
    selectedMake: classicMake,
    selectedModel: classicModel,
    selectedTrim: classicTrim,
    setMake: setClassicMake,
    setModel: setClassicModel,
    setTrim: setClassicTrim,
    setYear: setClassicYear,
    setModels: setClassicModels,
    setTrims: setClassicTrims,
    setYears: setClassicYears,
    isModern: false,
  })

  // Modern vehicle selection handlers
  const {
    makes: modernMakes,
    onSelectMake: onModernSelectMake,
    onSelectModel: onModernSelectModel,
    onSelectTrim: onModernSelectTrim,
  } = useVehicleSelectionHandlers({
    cars: modernCars,
    selectedMake: modernMake,
    selectedModel: modernModel,
    selectedTrim: modernTrim,
    setMake: setModernMake,
    setModel: setModernModel,
    setTrim: setModernTrim,
    setYear: setModernYear,
    setModels: setModernModels,
    setTrims: setModernTrims,
    setYears: setModernYears,
    isModern: true,
  })

  const handleClassicBuild = () => {
    if (onRequestCloseMenu) {
      onRequestCloseMenu()
    }
    window.scrollTo({ top: 20, behavior: "smooth" })

    if (classicTrim) {
      router.push(
        getCarsMakeModelYearTrimUrl(
          classicMake,
          classicModel,
          classicYear,
          classicTrim
        )
      )
    } else {
      router.push(
        getCarsMakeModelYearUrl(classicMake, classicModel, classicYear)
      )
    }
  }

  const handleModernBuild = async () => {
    // If using upholstery cars, save the selection to the store and redirect to upholstery
    if (isUsingUpholsteryCars && modernMake && modernModel && modernYear) {
      setModernLoading(true)
      try {
        const success = await saveVehicle(modernMake, modernModel, modernYear)

        if (success) {
          // Don't close menu - let page navigation handle it naturally
          window.scrollTo({ top: 20, behavior: "smooth" })
          // Navigate to inventory landing with YMM preselected
          router.push(
            buildUpholsteryLandingHref(modernMake, modernModel, modernYear)
          )
          return
        }

        // Modern/upholstery vehicles must not fall back to /cars/...
        if (process.env.NODE_ENV === "development") {
          console.warn(
            `Could not load upholstery config for ${modernMake}/${modernModel}/${modernYear}. Not redirecting to /cars.`
          )
        }
        return
      } finally {
        setModernLoading(false)
      }
    }

    // Fallback: classic/layout cars only (not upholstery modern flow)
    if (onRequestCloseMenu) {
      onRequestCloseMenu()
    }
    window.scrollTo({ top: 20, behavior: "smooth" })

    if (modernTrim) {
      router.push(
        getCarsMakeModelYearTrimUrl(
          modernMake,
          modernModel,
          modernYear,
          modernTrim
        )
      )
    } else {
      router.push(getCarsMakeModelYearUrl(modernMake, modernModel, modernYear))
    }
  }

  const handlePopularBuildClick = async (
    build: any,
    variant: "classic" | "modern"
  ) => {
    // If it's a modern build using upholstery cars, save to store and redirect to upholstery
    // Also check that upholstery cars are actually loaded
    if (
      variant === "modern" &&
      isUsingUpholsteryCars &&
      upholsteryCars.length > 0
    ) {
      setModernLoading(true)
      try {
        const success = await saveVehicle(build.make, build.model, build.year)

        if (success) {
          // Don't close menu - let page navigation handle it naturally
          window.scrollTo({ top: 20, behavior: "smooth" })
          // Navigate to inventory landing with YMM preselected
          router.push(
            buildUpholsteryLandingHref(build.make, build.model, build.year)
          )
          return
        }

        if (process.env.NODE_ENV === "development") {
          console.warn(
            `Could not load upholstery config for ${build.make}/${build.model}/${build.year}. Not redirecting to /cars.`
          )
        }
        return
      } finally {
        setModernLoading(false)
      }
    }

    // Fallback: classic builds, or modern when not using upholstery cars
    if (onRequestCloseMenu) {
      onRequestCloseMenu()
    }
    window.scrollTo({ top: 20, behavior: "smooth" })
    const year_type = build.type ? `${build.year}/${build.type}` : build.year
    router.push(getCarsMakeModelYearUrl(build.make, build.model, year_type))
  }

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue)
  }

  const reset = () => {
    setClassicMake("")
    setClassicModel("")
    setClassicTrim("")
    setClassicYear("")
    setClassicModels([])
    setClassicTrims([])
    setClassicYears([])

    setModernMake("")
    setModernModel("")
    setModernTrim("")
    setModernYear("")
    setModernModels([])
    setModernTrims([])
    setModernYears([])
  }

  React.useEffect(() => {
    if (!isOpen) {
      reset()
      setActiveTab(defaultActiveTab)
    }
  }, [isOpen, defaultActiveTab])

  const isClassicBuildDisabled =
    !classicMake ||
    !classicModel ||
    (classicTrims.length > 0 && !classicTrim) ||
    !classicYear

  const isModernBuildDisabled =
    modernLoading ||
    catalogLoading ||
    !modernMake ||
    !modernModel ||
    (modernTrims.length > 0 && !modernTrim) ||
    !modernYear

  return (
    <>
      <Overlay isVisible={isOpen} />
      <Container isOpen={isOpen}>
        <Header>
          <VerticalSpace height={30} />
          <CloseButton onClick={onRequestClose}>
            <IconWrapper>
              <Close />
            </IconWrapper>
          </CloseButton>
        </Header>
        <Body>
          <Label>
            <div>Shop by vehicle</div>
            <h1>Let's start by project</h1>
          </Label>
          <TabsContainer>
            <StyledTabs
              value={activeTab}
              onChange={handleTabChange}
              aria-label="vehicle type tabs"
            >
              <StyledTab label="1947-2017" />
              <StyledTab
                label={
                  <span style={{ whiteSpace: "nowrap", textTransform: "none" }}>
                    2018 - PRESENT
                  </span>
                }
              />
            </StyledTabs>
          </TabsContainer>
          <Content>
            {activeTab === 0 && (
              <>
                <Filters>
                  <Filter>
                    <SelectInput
                      name="Classic-Make"
                      label="Make"
                      value={classicMake}
                      options={classicMakes}
                      onChange={(e) => {
                        onClassicSelectMake(e.target.value)
                      }}
                    />
                  </Filter>
                  <Filter disabled={!classicMake}>
                    <SelectInput
                      name="Classic-Model"
                      label="Model"
                      value={classicModel}
                      options={classicModels}
                      onChange={(e) => {
                        onClassicSelectModel(e.target.value)
                      }}
                    />
                  </Filter>
                  {classicTrims && classicTrims.length > 0 && (
                    <Filter disabled={!classicMake || !classicModel}>
                      <SelectInput
                        name="Classic-Trim"
                        label="Trim"
                        value={classicTrim}
                        options={classicTrims}
                        onChange={(e) => {
                          onClassicSelectTrim(e.target.value)
                        }}
                      />
                    </Filter>
                  )}
                  <Filter
                    disabled={
                      !classicMake ||
                      !classicModel ||
                      (classicTrims.length > 0 && !classicTrim)
                    }
                  >
                    <SelectInput
                      name="Classic-Year"
                      label="Year"
                      value={classicYear}
                      options={classicYears}
                      onChange={(e) => {
                        setClassicYear(e.target.value)
                      }}
                    />
                  </Filter>
                  <button
                    disabled={isClassicBuildDisabled}
                    onClick={handleClassicBuild}
                  >
                    Build
                  </button>
                </Filters>
                {classicPopularBuilds.length > 0 && (
                  <PopularBuildsContainer>
                    <PopularBuildsLabel>
                      Popular classic builds:
                    </PopularBuildsLabel>
                    <PopularBuildsList>
                      {classicPopularBuilds.map((build, index) => (
                        <PopularBuildButton
                          key={index}
                          variant="classic"
                          onClick={() =>
                            handlePopularBuildClick(build, "classic")
                          }
                        >
                          {build.label}
                        </PopularBuildButton>
                      ))}
                    </PopularBuildsList>
                  </PopularBuildsContainer>
                )}
              </>
            )}
            {activeTab === 1 && (
              <>
                <Filters>
                  <Filter disabled={catalogLoading}>
                    <SelectInput
                      name="Modern-Make"
                      label="Make"
                      value={modernMake}
                      options={modernMakes}
                      onChange={(e) => {
                        onModernSelectMake(e.target.value)
                      }}
                    />
                  </Filter>
                  <Filter disabled={catalogLoading || !modernMake}>
                    <SelectInput
                      name="Modern-Model"
                      label="Model"
                      value={modernModel}
                      options={modernModels}
                      onChange={(e) => {
                        onModernSelectModel(e.target.value)
                      }}
                    />
                  </Filter>
                  {modernTrims && modernTrims.length > 0 && (
                    <Filter
                      disabled={catalogLoading || !modernMake || !modernModel}
                    >
                      <SelectInput
                        name="Modern-Trim"
                        label="Trim"
                        value={modernTrim}
                        options={modernTrims}
                        onChange={(e) => {
                          onModernSelectTrim(e.target.value)
                        }}
                      />
                    </Filter>
                  )}
                  <Filter
                    disabled={
                      catalogLoading ||
                      !modernMake ||
                      !modernModel ||
                      (modernTrims.length > 0 && !modernTrim)
                    }
                  >
                    <SelectInput
                      name="Modern-Year"
                      label="Year"
                      value={modernYear}
                      options={modernYears}
                      onChange={(e) => {
                        setModernYear(e.target.value)
                      }}
                    />
                  </Filter>
                  <div className="flex items-center gap-2">
                    <button
                      disabled={isModernBuildDisabled}
                      onClick={handleModernBuild}
                    >
                      {modernLoading || catalogLoading ? "Loading..." : "Build"}
                    </button>
                    {catalogLoading ? (
                      <div
                        className="animate-spin h-5 w-5 border-2 border-gray-400 border-t-transparent rounded-full shrink-0"
                        aria-label="Loading vehicles"
                      />
                    ) : null}
                  </div>
                </Filters>
                {modernPopularBuilds.length > 0 && (
                  <PopularBuildsContainer>
                    <PopularBuildsLabel>
                      Popular modern builds:
                    </PopularBuildsLabel>
                    <PopularBuildsList>
                      {modernPopularBuilds.map((build, index) => (
                        <PopularBuildButton
                          key={index}
                          variant="modern"
                          onClick={() =>
                            handlePopularBuildClick(build, "modern")
                          }
                        >
                          {build.label}
                        </PopularBuildButton>
                      ))}
                    </PopularBuildsList>
                  </PopularBuildsContainer>
                )}
              </>
            )}
          </Content>
        </Body>
        <VerticalSpace height={40} />
        <Footer>
          <IconWrapper>
            <Info />
          </IconWrapper>
          <span>
            Don't see your vehicle? Give us a call &nbsp;
            <a href="tel:1-888-460-0640">888-460-0640</a>
          </span>
        </Footer>
      </Container>
    </>
  )
}

export default CarSelector
